import { useState, useEffect, useCallback } from 'react'
import { ChatPanel } from './components/chat/ChatPanel'
import { PreviewPanel } from './components/preview/PreviewPanel'
import { useLatexCompiler } from './hooks/useLatexCompiler'
import { useMemory } from './hooks/useMemory'
import { useChat } from './hooks/useChat'
import { useDocumentUpload } from './hooks/useDocumentUpload'
import { defaultLatexSource } from './data/defaultLatex'
import { loadLatex, saveLatex } from './services/memoryStorage'
import styles from './App.module.css'

function App() {
  const [viewMode, setViewMode] = useState<'pdf' | 'code'>('pdf')
  // Load from localStorage if available, otherwise show Jake Ryan template as placeholder
  const [latexSource, setLatexSource] = useState(() => loadLatex() ?? defaultLatexSource)
  const [pageCount, setPageCount] = useState<number | null>(null)

  const { compile, pdfUrl, isCompiling, error } = useLatexCompiler({
    debounceMs: 1000,
    autoCompile: true,
  })

  const { memory, updateMemory } = useMemory()

  const handleLatexUpdate = useCallback(
    (newLatex: string) => {
      setLatexSource(newLatex)
      saveLatex(newLatex) // Persist to localStorage
      compile(newLatex)
    },
    [compile]
  )

  const { messages, sendMessage, sendDocument, isLoading } = useChat({
    memory,
    currentLatex: latexSource,
    pageCount,
    onMemoryUpdate: updateMemory,
    onLatexUpdate: handleLatexUpdate,
  })

  // Document upload handling
  const {
    isUploading,
    fileInputRef,
    openFilePicker,
    handleInputChange,
    acceptTypes,
  } = useDocumentUpload({
    onDocumentParsed: sendDocument,
    onError: (error) => {
      console.error('Document upload error:', error)
      // Show error to user via alert (could be replaced with toast component)
      alert(`Upload failed: ${error}`)
    },
  })

  // Initial compilation on mount
  useEffect(() => {
    compile(latexSource)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Handle manual LaTeX edits
  const handleLatexChange = (newSource: string) => {
    setLatexSource(newSource)
    saveLatex(newSource) // Persist manual edits too
    compile(newSource)
  }

  // Handle page count updates from PDF viewer
  const handlePageCountChange = useCallback((count: number) => {
    setPageCount(count)
  }, [])

  // Handle chat message send
  const handleSend = (content: string) => {
    sendMessage(content)
  }

  return (
    <div className={styles.container}>
      <ChatPanel
        messages={messages}
        isTyping={isLoading}
        onSend={handleSend}
        onUploadClick={openFilePicker}
        isUploading={isUploading}
        fileInputRef={fileInputRef}
        onFileInputChange={handleInputChange}
        acceptTypes={acceptTypes}
      />
      <PreviewPanel
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        latexSource={latexSource}
        onLatexChange={handleLatexChange}
        pdfUrl={pdfUrl}
        isCompiling={isCompiling}
        compilationError={error}
        onPageCountChange={handlePageCountChange}
      />
    </div>
  )
}

export default App
